import firebaseApp from '../firebase/index';
import { getCurrentUser, getPublicKey } from './user.service';
import CustomError from './errors/CustomError';
import { status_response } from './constants';
import { getProductDefaultProperty, getProductQuantity } from './properties.service';
import { getProductStockById } from './product.service';
import { getStorageAffiliator, removeStorageAffiliator } from './affiliate.service';
import Products from '../sidechain/handles/productHandle';
import { setLocalStorage, getLocalStorage } from '@/utils/commonUtil';

const cartRef = firebaseApp.firestore().collection('cart-info');

const initFirebaseCart = async function(userId) {
  const { id } = await cartRef.add({
    carts: [],
    userId: userId
  });
  // Update into firebase Users collection
  await firebaseApp
    .firestore()
    .collection('users')
    .doc(userId)
    .update({ cartId: id });
  return id;
};

const getDataCarts = async function(user) {
  try {
    if (!user) {
      user = await getCurrentUser();
    }
    let { uid: userId } = user;
    let cartDocId;
    let carts = [];
    const querySnapshot = await cartRef.where('userId', '==', userId).get();

    if (!querySnapshot.size) {
      cartDocId = await initFirebaseCart(userId);
      carts = [];
    } else {
      querySnapshot.forEach(doc => {
        cartDocId = doc.id;
        carts = doc.data().carts;
      });
    }

    return {
      status: status_response.success,
      result: {
        data: {
          cartDocId: cartDocId,
          carts
        }
      }
    };
  } catch (error) {
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: error.messageId
      }
    };
  }
};

/**
 * Function to update cart
 * @param product product object
 * @param amount
 */
const addToCart = async function(product, amount = 1, propId = null) {
  try {
    const productId = product.id;
    let affiliator = getStorageAffiliator(productId);
    const currentPublicAddress = await getPublicKey();
    console.log('Add to cart', affiliator, currentPublicAddress, product.owner);
    if (affiliator && currentPublicAddress && (affiliator === currentPublicAddress || affiliator === product.owner)) {
      affiliator = null;
    }

    // Check product is available to add
    const productHandle = new Products();
    const statusAvailable = await productHandle.isProductAvailAndPublished(productId);
    if (!statusAvailable) {
      throw new CustomError('Product not available', 'EF2151_Content');
    }

    let { status, result } = await getDataCarts();
    if (status === status_response.error) {
      throw new CustomError(result.message, result.messageId);
    }
    try {
      // Refresh product quantity from sidechain to have the latest update
      product.properties = await getProductStockById(product.id);
      product.quantity = product.properties.stock_num;
    } catch (err) {
      console.log('Cannot fetch product properties from sidechain, using old data', err);
    }

    let currentProperty = propId;
    if (!propId && propId !== 0) {
      currentProperty = getProductDefaultProperty(product);
    }

    let productInCarts = false;
    const dataCart = result.data;
    for (let i in dataCart.carts) {
      if (dataCart.carts[i].productId === productId && dataCart.carts[i].propId === currentProperty) {
        const remainQuantity = getProductQuantity(product, currentProperty);
        if (remainQuantity === -1) {
          throw new CustomError('This product is updated.', 'EF2152_Content');
        } else if (dataCart.carts[i].quantity + amount > remainQuantity) {
          throw new CustomError('Quantity of product is not enough to add cart.', 'EA2011_Content');
        }

        if (affiliator) {
          dataCart.carts[i].affiliator = affiliator;
        } else if (!dataCart.carts[i].affiliator) {
          dataCart.carts[i].affiliator = null;
        }

        dataCart.carts[i].quantity += amount;
        dataCart.carts[i].updatedAt = new Date().getTime();
        productInCarts = true;
        break;
      }
    }

    if (productInCarts) {
      await cartRef.doc(dataCart.cartDocId).update({
        carts: dataCart.carts
      });
    } else {
      const timestamp = new Date();
      const remainQuantity = getProductQuantity(product, currentProperty);
      console.log('Remain Quantity: ', remainQuantity);
      if (remainQuantity === -1) {
        throw new CustomError('This product is updated.', 'EF2152_Content');
      } else if (!remainQuantity || amount > remainQuantity) {
        throw new CustomError('Quantity of product is not enough to add cart.', 'EA2011_Content');
      }
      const cartItem = {
        productId,
        affiliator,
        quantity: amount,
        propId: currentProperty,
        createdAt: timestamp,
        updatedAt: timestamp
      };

      if (dataCart.carts) {
        dataCart.carts.unshift(cartItem);
      } else {
        dataCart.carts = [cartItem];
      }
      console.log('carts:', dataCart.carts);
      await cartRef.doc(dataCart.cartDocId).update({ carts: dataCart.carts });
    }

    removeStorageAffiliator(productId);

    return {
      status: status_response.success,
      result: {
        data: dataCart.carts,
        messageId: 'EF2023_Content'
      }
    };
  } catch (error) {
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: error.messageId
      }
    };
  }
};

const changeProductQuantity = async function(productId, propId, amount, totalQuantityProduct) {
  try {
    // Check product is available to add
    const productHandle = new Products();
    const statusAvailable = await productHandle.isProductAvailAndPublished(productId);
    if (!statusAvailable) {
      throw new CustomError('Product not available', 'EF2151_Content');
    }

    let { status, result } = await getDataCarts();
    if (status === status_response.error) {
      throw new CustomError(result.message, result.messageId);
    }
    let dataCart = result.data;
    for (let i in dataCart.carts) {
      if (dataCart.carts[i].productId === productId && dataCart.carts[i].propId === propId) {
        if (amount < 0 && dataCart.carts[i].quantity + amount > totalQuantityProduct) {
          //Case subtract product -- Mr.Cong said
        } else if (dataCart.carts[i].quantity + amount > totalQuantityProduct) {
          throw new CustomError('Quantity of product is not enough to add cart.', 'EA2011_Content');
        }
        if (dataCart.carts[i].quantity + amount < 1) {
          throw new CustomError('Minimum quantity in product must be equal 1', 'EF2047_Content');
        }
        dataCart.carts[i].quantity += amount;
        dataCart.carts[i].updatedAt = new Date().getTime();
        break;
      }
    }
    await cartRef.doc(dataCart.cartDocId).update({
      carts: dataCart.carts
    });

    return {
      status: status_response.success,
      result: {
        data: dataCart.carts,
        messageId: 'EF2046_Content'
      }
    };
  } catch (error) {
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: error.messageId
      }
    };
  }
};

const removeProductInCart = async function(productId, propId, from_cart = false) {
  try {
    let { status, result } = await getDataCarts();
    if (status === status_response.error) {
      throw new CustomError(result.message, result.messageId);
    }
    let dataCart = result.data;
    let carts = dataCart.carts.filter(
      item => item.productId !== productId || (propId ? propId !== item.propId : false)
    );
    await cartRef.doc(dataCart.cartDocId).update({
      carts: carts
    });
    let messageId;
    if (from_cart) {
      messageId = 'EF2046_Content';
    } else {
      messageId = 'EF2024_Content';
    }
    return {
      status: status_response.success,
      result: {
        data: carts,
        messageId: messageId
      }
    };
  } catch (error) {
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: error.messageId || 'EF2047_Content'
      }
    };
  }
};

const removeProductByIds = async function(ids, from_cart = false) {
  try {
    let { status, result } = await getDataCarts();
    if (status === status_response.error) {
      throw new CustomError(result.message, result.messageId);
    }
    let dataCart = result.data;
    let carts = dataCart.carts.filter(item => {
      return ids.indexOf(item.productId) === -1;
    });
    await cartRef.doc(dataCart.cartDocId).update({
      carts: carts
    });
    let messageId;
    if (from_cart) {
      messageId = 'EF2046_Content';
    } else {
      messageId = 'EF2024_Content';
    }
    return {
      status: status_response.success,
      result: {
        data: carts,
        messageId: messageId
      }
    };
  } catch (error) {
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: error.messageId || 'EF2047_Content'
      }
    };
  }
};

const resetCart = async function() {
  try {
    let { status, result } = await getDataCarts();
    if (status === status_response.error) {
      throw new CustomError(result.message, result.messageId);
    }
    let dataCart = result.data;
    await cartRef.doc(dataCart.cartDocId).update({
      carts: []
    });
    return {
      status: status_response.success,
      result: {
        data: [],
        messageId: 'success'
      }
    };
  } catch (error) {
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: error.messageId
      }
    };
  }
};

export { getDataCarts, addToCart, changeProductQuantity, removeProductInCart, removeProductByIds, resetCart };
